// Requêtes des écrans de la propreté (TanStack Query). Clés sous « proprete » ; le changement de
// contexte vide le cache (FournisseurSession).
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import type {
  Calendrier,
  CarteProprete,
  Cout,
  CreationCout,
  CreationPoint,
  CreationPrestataire,
  CreationSecteur,
  CreationTournee,
  CreationVehicule,
  DepotsSauvages,
  Liste,
  ListeCouts,
  ModificationTournee,
  PointCollecte,
  Prestataire,
  Realisation,
  SaisieRealisation,
  Secteur,
  SecteurDetail,
  Tournee,
  TourneeDetail,
  Vehicule,
} from './types';

export const INVALIDER_PROPRETE: readonly (readonly unknown[])[] = [['proprete'], ['indicateurs']];

const liste = <T,>(chemin: string, parametres: Record<string, string | undefined>, signal?: AbortSignal) => requete<Liste<T>>(chemin, { signal, parametres }).then((r) => r.elements);

export function useSecteurs(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['proprete', 'secteurs', filtres], queryFn: ({ signal }) => liste<Secteur>('/proprete/secteurs', filtres, signal), retry: false });
}

export function useSecteur(id: string | null) {
  return useQuery({ queryKey: ['proprete', 'secteur', id ?? ''], queryFn: ({ signal }) => requete<SecteurDetail>(`/proprete/secteurs/${id}`, { signal }), enabled: Boolean(id), retry: false });
}

export function usePoints(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['proprete', 'points', filtres], queryFn: ({ signal }) => liste<PointCollecte>('/proprete/points', filtres, signal), retry: false });
}

export function useVehicules() {
  return useQuery({ queryKey: ['proprete', 'vehicules'], queryFn: ({ signal }) => liste<Vehicule>('/proprete/vehicules', {}, signal), retry: false });
}

export function usePrestataires() {
  return useQuery({ queryKey: ['proprete', 'prestataires'], queryFn: ({ signal }) => liste<Prestataire>('/proprete/prestataires', {}, signal), retry: false });
}

export function useTournees(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['proprete', 'tournees', filtres], queryFn: ({ signal }) => liste<Tournee>('/proprete/tournees', filtres, signal), retry: false });
}

export function useTournee(id: string | null) {
  return useQuery({ queryKey: ['proprete', 'tournee', id ?? ''], queryFn: ({ signal }) => requete<TourneeDetail>(`/proprete/tournees/${id}`, { signal }), enabled: Boolean(id), retry: false });
}

/** Semaine du jour donné (lundi au dimanche) ; vide pour la semaine courante. */
export function useCalendrier(semaine: string) {
  return useQuery({ queryKey: ['proprete', 'calendrier', semaine], queryFn: ({ signal }) => requete<Calendrier>('/proprete/calendrier', { signal, parametres: semaine ? { semaine } : {} }), retry: false });
}

export function useRealisations(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['proprete', 'realisations', filtres], queryFn: ({ signal }) => liste<Realisation>('/proprete/realisations', filtres, signal), retry: false });
}

export function useCouts(filtres: Record<string, string> = {}) {
  return useQuery({ queryKey: ['proprete', 'couts', filtres], queryFn: ({ signal }) => requete<ListeCouts>('/proprete/couts', { signal, parametres: filtres }), retry: false });
}

export function useDepotsSauvages() {
  return useQuery({ queryKey: ['proprete', 'depots-sauvages'], queryFn: ({ signal }) => requete<DepotsSauvages>('/proprete/depots-sauvages', { signal }), retry: false });
}

/** Couche des secteurs (polygones) et des points de collecte, filtrée par l'emprise affichée. */
export function useCarteProprete(bbox: string | null, actif = true) {
  return useQuery({ queryKey: ['proprete', 'carte', bbox ?? ''], queryFn: ({ signal }) => requete<CarteProprete>('/carte/proprete', { signal, parametres: { bbox } }), enabled: actif && Boolean(bbox), retry: false });
}

// Écritures ---------------------------------------------------------------------------------

export const creerSecteur = (corps: CreationSecteur) => requete<SecteurDetail>('/proprete/secteurs', { methode: 'POST', corps });
export const creerPoint = (corps: CreationPoint) => requete<PointCollecte>('/proprete/points', { methode: 'POST', corps });
export const creerVehicule = (corps: CreationVehicule) => requete<Vehicule>('/proprete/vehicules', { methode: 'POST', corps });
export const creerPrestataire = (corps: CreationPrestataire) => requete<Prestataire>('/proprete/prestataires', { methode: 'POST', corps });
export const creerTournee = (corps: CreationTournee) => requete<TourneeDetail>('/proprete/tournees', { methode: 'POST', corps });
export const modifierTournee = (id: string, corps: ModificationTournee) => requete<TourneeDetail>(`/proprete/tournees/${id}`, { methode: 'PATCH', corps });
export const enregistrerRealisation = (tourneeId: string, corps: SaisieRealisation) => requete<Realisation>(`/proprete/tournees/${tourneeId}/realisations`, { methode: 'POST', corps });
export const creerCout = (corps: CreationCout) => requete<Cout>('/proprete/couts', { methode: 'POST', corps });
